import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { ModalDataItem } from "../../../ducks/app";
import { Button } from "../../atoms/Button";
import { CloseIcon } from "../../atoms/icons/CloseIcon";

type Props = ModalDataItem & {
  isOpen: boolean;
  defaultCloseModal?: () => void;
};

export const PopupModal: React.FC<Props> = ({
  title,
  subTitle,
  buttons,
  isOpen,
  defaultCloseModal,
  showCloseButton,
}) => {
  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original body and document element styles
      const body = document.body;
      const documentElement = document.documentElement;

      const originalBodyOverflow = body.style.overflow;
      const originalBodyPosition = body.style.position;
      const originalBodyTop = body.style.top;
      const originalBodyWidth = body.style.width;
      const originalDocumentOverflow = documentElement.style.overflow;

      // Get current scroll position
      const scrollY = window.scrollY;
      const scrollBarWidth = window.innerWidth - documentElement.clientWidth;

      // Detect Safari version for compatibility
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const safariVersion = isSafari ? parseInt(navigator.userAgent.match(/version\/(\d+)/i)?.[1] || '0') : 0;

      // Check if 100dvh is supported
      const supports100dvh = CSS.supports('height', '100dvh');

      // Apply scroll lock with Safari 12 compatibility
      body.style.overflow = 'hidden';

      // Only use the old approach for Safari 12 specifically
      // Safari 13+ and all other modern browsers should use the modern approach
      if (isSafari && safariVersion === 12) {
        // For Safari 12 specifically, use a simpler approach that doesn't break modal positioning
        body.style.position = 'relative';
        body.style.top = '0';
        body.style.width = '100%';
        body.style.paddingRight = `${scrollBarWidth}px`;
      } else {
        // Modern approach for Safari 13+, Chrome, Firefox, Edge, and all other browsers
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        body.style.paddingRight = `${scrollBarWidth}px`;
      }

      documentElement.style.overflow = 'hidden';

      // Prevent touch scrolling on iOS - but only outside modal content
      const preventTouchMove = (e: TouchEvent) => {
        // Allow scrolling within modal content
        const target = e.target as Element;
        const modalContent = document.querySelector('[data-popup-modal-content]');
        if (modalContent && modalContent.contains(target)) {
          return; // Allow scrolling within modal
        }
        e.preventDefault();
      };

      document.addEventListener('touchmove', preventTouchMove, { passive: false });

      // Cleanup function to restore original state
      return () => {
        body.style.overflow = originalBodyOverflow;
        body.style.position = originalBodyPosition;
        body.style.top = originalBodyTop;
        body.style.width = originalBodyWidth;
        body.style.paddingRight = '';
        documentElement.style.overflow = originalDocumentOverflow;

        document.removeEventListener('touchmove', preventTouchMove);

        // Restore scroll position for all browsers except Safari 12
        if (!(isSafari && safariVersion === 12)) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && defaultCloseModal) {
        defaultCloseModal();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, defaultCloseModal]);

  if (!isOpen) return null;

  // Handle background click to close modal
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && defaultCloseModal) {
      defaultCloseModal();
    }
  };

  return (
    <StyledModalBackground $isOpen={isOpen} onClick={handleBackgroundClick}>
      <StyledModalContainer $isOpen={isOpen} data-popup-modal-content>
        <StyledHeader>
          <StyledTitleWrapper><StyledModalTitle>{title}</StyledModalTitle>
            <StyledModalSubTitle>{subTitle}</StyledModalSubTitle></StyledTitleWrapper>

          {(showCloseButton !== false && defaultCloseModal) && (
            <StyledCloseButton onClick={defaultCloseModal}>
              <CloseIcon />
            </StyledCloseButton>
          )}
        </StyledHeader>

        <StyledFooter>
          {buttons.map((button, index) => (
            <Button
              key={index}
              text={button.text}
              variant={button.variant}
              onClick={button.onClick ?? defaultCloseModal}
            />
          ))}
        </StyledFooter>
      </StyledModalContainer>
    </StyledModalBackground>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9, 0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1, 1);
  }
`;

const StyledModalBackground = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh; /* Fallback for older browsers */
  height: 100dvh; /* Modern browsers - will override 100vh if supported */
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9998;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: all 0.5s ease;
  
  /* Only apply -webkit-fill-available for browsers that don't support 100dvh */
  @supports not (height: 100dvh) {
    height: -webkit-fill-available;
  }
`;

const StyledModalContainer = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  max-width: 512px;
  padding: 28px 32px;
  border-radius: 8px;
  ${({ $isOpen }) => ($isOpen ? "" : "display: none;")}
  background-color: white;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  box-shadow: 4px 6px 10px 0px rgba(0, 0, 0, 0.1);
  transform: ${({ $isOpen }) =>
    $isOpen
      ? "translate(-50%, -50%) scale(1, 1)"
      : "translate(-50%, -50%) scale(0.9, 0.9)"};
  animation: ${fadeIn} 0.5s forwards;
  max-height: 90vh;
  overflow-y: auto;
  
  /* Safari 12 specific positioning fixes - only apply to browsers without 100dvh support */
  @supports not (height: 100dvh) {
    /* Ensure modal stays centered in Safari 12 and other older browsers */
    position: fixed;
    top: 50%;
    left: 50%;
    transform: ${({ $isOpen }) =>
    $isOpen
      ? "translate(-50%, -50%) scale(1, 1)"
      : "translate(-50%, -50%) scale(0.9, 0.9)"};
    /* Add hardware acceleration for smoother rendering */
    -webkit-backface-visibility: hidden;
    -webkit-perspective: 1000;
  }

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 24px 20px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    max-width: 95%;
    padding: 20px 16px;
    border-radius: 4px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const StyledTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const StyledModalTitle = styled.div`
  max-width: 390px;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
  text-align: center;

  @media (max-width: 768px) {
    max-width: 300px;
    font-size: 24px;
  }

  @media (max-width: 480px) {
    max-width: 250px;
    font-size: 20px;
  }
`;
const StyledModalSubTitle = styled.div`
  max-width: 390px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-line;
  text-align: center;

  @media (max-width: 768px) {
    max-width: 300px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    max-width: 250px;
    font-size: 13px;
  }
`;

const StyledCloseButton = styled.div`
  position: absolute;
  top: 9px;
  right: 12px;
  cursor: pointer;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;
